import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { ArcButton, ArcIconButton, ArcStack, ArcText } from "@src/core/ui";
import { FormState, useForm } from "react-hook-form";
import { ICreateZoneStage1Form } from "./types";
import { useNavigate } from "react-router-dom";
import { setZoneMapLocationUponCreation, useCreateZoneMutation } from "@src/features/zones/redux";
import { routesConfig } from "@src/core/routes";
import { NoFlyZoneForm } from "@src/features/zones/components/zone-form/no-fly";
import { useDispatch, useSelector } from "react-redux";
import { ZoneShapeType, ZoneType } from "@src/core/features";
import { selectMapLocation } from "@src/core/controllers";
import type { ZoneVariant } from "@src/features/zones/pages/zones-page/types";
import { VARIANT_NO_FLY, VARIANT_SAFE_LAND } from "@src/features/zones/pages/zones-page/types";
import { trackError, toTrackedError } from "@src/core/sentry";

export interface CreateZoneStage1PageProps {
  variant?: ZoneVariant;
}

export const CreateZoneStage1Page: React.FC<CreateZoneStage1PageProps> = ({ variant = VARIANT_NO_FLY }) => {

  const defaultValues = {
    zoneName: '',
    zoneDescription: '',
    latitude: '',
    longitude: '',
    address: '',
    zoneRadius: 0,
    zoneType: ZoneType.TEMPORARY,
  } as ICreateZoneStage1Form;

  const { control, setValue, register, getValues, formState: { errors }, handleSubmit, watch } = useForm<ICreateZoneStage1Form>({ defaultValues });

  const navigate = useNavigate();
  const [createZone, { isLoading }] = useCreateZoneMutation();

  const dispatch = useDispatch();
  const location = useSelector(selectMapLocation);
  useEffect(() => {
    dispatch(setZoneMapLocationUponCreation({
      id: watch('id') ?? 0,
      latitude: location?.latitude ?? 0,
      longitude: location?.longitude ?? 0,
      zoneName: watch('zoneName') ?? '',
      radius: watch('zoneRadius') ?? 0,
      type: watch('zoneType') ?? ZoneType.TEMPORARY,
      variant: VARIANT_NO_FLY,
    }));
    return () => {
      dispatch(setZoneMapLocationUponCreation(null));
    };
  }, [location, watch]);

  const onCancel =()=> { 
    navigate(-1);
    dispatch(setZoneMapLocationUponCreation(null));
  };

  const onSubmit = async (data: ICreateZoneStage1Form) => {
    try {
      const radiusMeters = data.zoneRadius;
      const payload = {
        name: data.zoneName,
        description: data.zoneDescription,
        centerLatitude: Number(data.latitude),
        centerLongitude: Number(data.longitude),
        locationName: data.address,
        radiusMeters,
        shape: ZoneShapeType.CIRCLE, 
        zoneType: data.zoneType,
        isActive: true,
        polygonCoordinates: [], 
      };

      await createZone(payload).unwrap();
      navigate(routesConfig.zones); 
      dispatch(setZoneMapLocationUponCreation(null));
    } catch (error: unknown) {
      console.error('Failed to create zone:', error);
      trackError(toTrackedError(error), { code: 'FE_0017', extra: { flow: 'createZoneStage1' } });
    }
  };

  const onDraw = () => {
    dispatch(setZoneMapLocationUponCreation(null));
    const basePath = variant === VARIANT_SAFE_LAND ? routesConfig.createSafeLandZone : routesConfig.createZone;
    navigate(`${basePath}?stage=2`);
  };

  const title = variant === VARIANT_SAFE_LAND ? 'CREATE SAFE-LAND ZONE' : 'CREATE NO-FLY ZONE';

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcStack direction='row' justifyContent='space-between' alignItems='center' sx={{ pr: 0.5 }}>
          <ArcText.h4 fontWeight={500}>{title}</ArcText.h4>
          <ArcIconButton size='small' onClick={onCancel} aria-label='Close' sx={{ p: 0 }}>
            <CloseIcon />
          </ArcIconButton>
        </ArcStack>

        <NoFlyZoneForm
          control={control}
          setValue={setValue}
          register={register}
          getValues={getValues}
          formState={{ errors } as FormState<ICreateZoneStage1Form>}
          watch={watch}
        />
      </ArcStack>

      <ArcStack spacing={1} direction='column'>
        <ArcStack spacing={1} direction='row' width='100%'>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onCancel}> 
          Cancel 
          </ArcButton.ghost>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onDraw}> 
          Draw Zone
          </ArcButton.ghost>
        </ArcStack>

        <ArcButton.brand 
          fullWidth
          size='large'
          type='submit'
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}> 
          Create
        </ArcButton.brand>    
      </ArcStack>
    </ArcStack>
  );
};
